/**
 * Núcleo del cliente admin: tipos de envelope, error tipado y helpers
 * `get` / `postJsonAdmin` que comparten todos los recursos.
 */

export interface AdminMeta {
  total?: number;
  nextCursor?: string;
  totalApprox?: number;
}

export interface AdminEnvelopeOk<T> {
  success: true;
  data: T;
  meta?: AdminMeta;
}

export interface AdminEnvelopeErr {
  success: false;
  error: { code: string; message: string; traceId?: string };
}

export type AdminEnvelope<T> = AdminEnvelopeOk<T> | AdminEnvelopeErr;

export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly traceId?: string,
  ) {
    // Adjuntamos el traceId al mensaje visible para que cualquier toast/banner
    // que muestre `err.message` ya traiga la pista para grepear el log del
    // backend, sin tener que tocar cada pantalla.
    super(traceId ? `${message} · trace ${traceId}` : message);
    this.name = 'AdminApiError';
  }
}

/**
 * Normaliza el body de error a `{ code, message, traceId? }`, tolerando las
 * dos formas que el backend puede emitir:
 *   - Envelope del BO: `{ success:false, error:{ code, message, traceId? } }`.
 *   - Forma plana (handler global / rutas legacy / gateways): `{ error: '...' }`
 *     donde `error` es un string. Antes esta forma rompía el parse
 *     (`json.error.code` sobre un string → undefined) y el BO mostraba vacío.
 */
function parseAdminError(json: unknown, status: number): { code: string; message: string; traceId?: string } {
  if (json && typeof json === 'object' && 'error' in json) {
    const e = json.error;
    if (e && typeof e === 'object') {
      const obj = e as { code?: unknown; message?: unknown; traceId?: unknown };
      const code = typeof obj.code === 'string' ? obj.code : 'http_error';
      const message = typeof obj.message === 'string' ? obj.message : `HTTP ${status}`;
      const traceId = typeof obj.traceId === 'string' ? obj.traceId : undefined;
      return traceId !== undefined ? { code, message, traceId } : { code, message };
    }
    if (typeof e === 'string') return { code: e, message: e };
  }
  return { code: 'http_error', message: `HTTP ${status}` };
}

/** Valida el envelope OK o tira `AdminApiError` con la mejor info disponible. */
async function unwrap<T>(res: Response): Promise<AdminEnvelopeOk<T>> {
  const json = (await res.json().catch(() => null)) as AdminEnvelope<T> | null;
  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const { code, message, traceId } = parseAdminError(json, res.status);
    throw new AdminApiError(res.status, code, message, traceId);
  }
  return json;
}

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

export async function get<T>(path: string, init?: RequestInit): Promise<AdminEnvelopeOk<T>> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...init,
  });
  return unwrap<T>(res);
}

async function sendJson<T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return (await unwrap<T>(res)).data;
}

export function postJsonAdmin<T>(path: string, body: unknown): Promise<T> {
  return sendJson<T>('POST', path, body);
}

export function patchJsonAdmin<T>(path: string, body: unknown): Promise<T> {
  return sendJson<T>('PATCH', path, body);
}

export function deleteJsonAdmin<T>(path: string, body: unknown): Promise<T> {
  return sendJson<T>('DELETE', path, body);
}
